import { useCallback, useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { calculateNights, getOrderGuestsCount } from 'entities/kyrgyzstan'

import { KyrgyzstanOrderData } from 'shared/api/kyrgyzstan/orders'
import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import { dateFormat } from 'shared/lib/dateFormat'
import { Paper } from 'shared/ui/paper'

import { BookingCardDetailsButton } from './ui/details-button'
import {
  BookingStatusDialogContent,
  type BookingStatusDialogItemView,
} from './ui/status-dialog'
import { BookingStatusHeader } from './ui/status-header'
import { BookingCardVoucherButton } from './ui/voucher-button'

import { useBookingStatusDialog } from './lib/use-booking-status-dialog'
import s from './styles.module.scss'

export interface KyrgyzstanBookingCardProps {
  order: KyrgyzstanOrderData
  onOpenDetails?: () => void
  onShowVoucher?: () => void
  className?: string
}

export const KyrgyzstanBookingCard = ({
  order,
  onOpenDetails,
  onShowVoucher,
  className,
}: KyrgyzstanBookingCardProps) => {
  const { t } = useTranslation()
  const {
    statusTranslationKey,
    statusTheme,
    dialogItems,
    dateOptions,
    openStatusDialog,
  } = useBookingStatusDialog({
    status: order.status,
    statusInfo: {
      arriveDate: order.start_date,
      outDate: order.end_date,
    },
    paymentStatus: order.payment_status,
  })

  const statusTitle = useMemo(
    () => t(statusTranslationKey),
    [statusTranslationKey, t],
  )

  const subtitle = useMemo(() => {
    const startFormatted = dateFormat(
      order.start_date,
      DATE_FORMAT_LIST.DATE_MONTH_SHORT,
    )
    const endFormatted = dateFormat(
      order.end_date,
      DATE_FORMAT_LIST.DATE_MONTH_SHORT,
    )

    const nightsCount = calculateNights(order.start_date, order.end_date)

    const nights = t('kyrgyzstan_block.search_result.nights', {
      count: nightsCount,
    })

    const visitors = t('kyrgyzstan_block.search_result.guests', {
      count: getOrderGuestsCount(order),
    })

    return `${startFormatted} – ${endFormatted}, ${nights}, ${visitors}`
  }, [order, t])

  const dialogViewItems = useMemo<BookingStatusDialogItemView[]>(
    () =>
      dialogItems.map((item) => ({
        icon: item.icon,
        text: item.withDates
          ? t(item.translationKey, dateOptions)
          : t(item.translationKey),
      })),
    [dateOptions, dialogItems, t],
  )

  const handleStatusClick = useCallback(() => {
    if (dialogViewItems.length === 0) {
      return
    }

    openStatusDialog(
      <BookingStatusDialogContent
        title={statusTitle}
        theme={statusTheme}
        items={dialogViewItems}
      />,
    )
  }, [dialogViewItems, openStatusDialog, statusTheme, statusTitle])

  return (
    <Paper
      padding={[12]}
      radius="16px"
      className={clsx(s.card, className)}
    >
      <BookingStatusHeader
        title={statusTitle}
        theme={statusTheme}
        onClick={handleStatusClick}
      />

      <BookingCardDetailsButton
        title={order.realty_object.name}
        subtitle={subtitle}
        onClick={onOpenDetails}
      />

      <div className={s.divider} />

      <BookingCardVoucherButton
        isEnabled={Boolean(order?.file_url)}
        onClick={onShowVoucher}
      />
    </Paper>
  )
}
